import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import Panel from '../components/ui/Panel'
import Chip from '../components/ui/Chip'
import Avatar from '../components/ui/Avatar'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import texts from '../texts'
import { createClub, getClubs } from '../lib/api'
import type { Club } from '../lib/api'

function Clubs() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [clubs, setClubs] = useState<Club[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    getClubs()
      .then(setClubs)
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      showToast(texts.clubs.missingName)
      return
    }
    setError('')
    setCreating(true)
    try {
      const club = await createClub(trimmed)
      navigate(`/klub/${club.id}`)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.clubs.genericCreateError)
    } finally {
      setCreating(false)
    }
  }

  return (
    <Screen>
      <h1>{texts.clubs.title}</h1>

      {loading ? (
        <p className="text-text-2">…</p>
      ) : clubs.length === 0 ? (
        <Panel>
          <b>{texts.clubs.emptyTitle}</b>
          <p className="text-[0.85em] text-text-2">{texts.clubs.emptyHint}</p>
        </Panel>
      ) : (
        <div className="grid gap-2.5">
          {clubs.map((club, i) => (
            <Link
              key={club.id}
              to={`/klub/${club.id}`}
              className="flex w-full items-center gap-2.5 rounded-xl border border-text-2/20 bg-text-2/10 px-3 py-2.5 text-inherit no-underline"
            >
              <Avatar name={club.name} index={i} />
              <div className="min-w-0 flex-1">
                <b className="block">{club.name}</b>
                <span className="text-[0.8em] text-text-2">{texts.clubs.members(club.memberCount)}</span>
              </div>
              <Chip variant={club.role === 'owner' ? 'gold' : 'neutral'}>
                {club.role === 'owner' ? texts.clubs.roleOwner : texts.clubs.roleMember}
              </Chip>
            </Link>
          ))}
        </div>
      )}

      <Panel>
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
          {texts.clubs.createTitle}
        </span>
        <form className="flex gap-2" onSubmit={handleCreate}>
          <Input
            placeholder={texts.clubs.namePlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" disabled={creating}>
            {creating ? texts.clubs.creating : texts.clubs.createButton}
          </Button>
        </form>
        {error && <p className="text-red">{error}</p>}
      </Panel>

      <BottomNav current="clubs" />
    </Screen>
  )
}

export default Clubs
